import { useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

import { Button } from "@/components/Button";
import { Table, type TableAction, type TableColumn } from "@/components/Table";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { Select, type SelectOption } from "@/components/Select";
import { Textarea } from "@/components/Textarea";
import { Badge } from "@/components/Badge";
import { StatCard } from "@/components/StatCard";
import { Title } from "@/components/Title";
import { Tabs } from "@/components/Tabs";
import { Alert } from "@/components/Alert";
import { Icon } from "@/components/Icon";

const translations = {
    page: {
        title: "Calendario de vacaciones",
        subtitle:
            "Consulta y gestiona tus vacaciones, permisos y ausencias del equipo",
        badge: "Gestión de ausencias",
        requestButton: "Nueva solicitud",
        todayButton: "Hoy",
        previousMonth: "Mes anterior",
        nextMonth: "Mes siguiente",
        currentMonth: "Junio 2026",
    },
    tabs: {
        calendar: "Calendario",
        requests: "Mis solicitudes",
        team: "Mi equipo",
    },
    stats: {
        available: "Días disponibles",
        used: "Días utilizados",
        pending: "Solicitudes pendientes",
        teamAbsences: "Ausencias del equipo",
        availableHelper: "Periodo actual",
        usedHelper: "De 23 días asignados",
        pendingHelper: "Requieren seguimiento",
        teamHelper: "Durante este mes",
    },
    calendar: {
        title: "Calendario de ausencias",
        subtitle:
            "Consulta las ausencias visibles y planifica tus próximos días",
        days: {
            day1: "1",
            day2: "2",
            day3: "3",
            day4: "4",
            day5: "5",
            day6: "6",
            day7: "7",
            day8: "8",
            day9: "9",
            day10: "10",
            day11: "11",
            day12: "12",
            day13: "13",
            day14: "14",
            day15: "15",
            day16: "16",
            day17: "17",
            day18: "18",
            day19: "19",
            day20: "20",
            day21: "21",
            day22: "22",
            day23: "23",
            day24: "24",
            day25: "25",
            day26: "26",
            day27: "27",
            day28: "28",
            day29: "29",
            day30: "30",
        },
        weekdays: {
            monday: "L",
            tuesday: "M",
            wednesday: "X",
            thursday: "J",
            friday: "V",
            saturday: "S",
            sunday: "D",
        },
        legend: {
            vacation: "Vacaciones",
            permission: "Permiso",
            pending: "Pendiente",
            holiday: "Festivo",
        },
        events: {
            maria: "María López · Vacaciones",
            carlos: "Carlos Martín · Permiso",
            holiday: "San Pedro y San Pablo",
        },
        monthSummary: "Resumen del mes",
        workingDays: "Días laborables",
        plannedAbsences: "Ausencias planificadas",
        holidays: "Festivos",
    },
    requests: {
        title: "Mis solicitudes",
        search: "Buscar solicitudes...",
        type: "Tipo",
        status: "Estado",
        allTypes: "Todos los tipos",
        allStatuses: "Todos los estados",
        vacation: "Vacaciones",
        permission: "Permiso",
        approved: "Aprobada",
        pending: "Pendiente",
        rejected: "Rechazada",
        startDate: "Fecha de inicio",
        endDate: "Fecha de fin",
        days: "Días",
        created: "Fecha de solicitud",
        reason: "Motivo",
        actions: {
            view: "Ver detalle",
            cancel: "Cancelar solicitud",
        },
    },
    team: {
        title: "Ausencias del equipo",
        member: "Empleado",
        department: "Departamento",
        absenceType: "Tipo de ausencia",
        period: "Periodo",
        teamStatus: "Estado",
    },
    form: {
        title: "Nueva solicitud",
        type: "Tipo de solicitud",
        typePlaceholder: "Selecciona un tipo",
        vacationOption: "Vacaciones",
        permissionOption: "Permiso retribuido",
        startDate: "Fecha de inicio",
        startDatePlaceholder: "Ejemplo: 15/07/2026",
        endDate: "Fecha de fin",
        endDatePlaceholder: "Ejemplo: 24/07/2026",
        reason: "Motivo o comentarios",
        reasonPlaceholder:
            "Indica cualquier información relevante para la solicitud",
        cancel: "Cancelar",
        submit: "Enviar solicitud",
        submitted: "La solicitud se ha enviado correctamente.",
    },
    filters: {
        type: "Filtrar por tipo",
        status: "Filtrar por estado",
    },
    empty: "No hay registros que mostrar",
};

const i18nInstance = i18next.addResourceBundle
    ? i18next
    : (i18next as any).default;
if (i18nInstance?.addResourceBundle) {
    i18nInstance.addResourceBundle("es", "global", translations, true, true);
    i18nInstance.addResourceBundle(
        "es",
        "translation",
        translations,
        true,
        true
    );
}

type RequestRow = {
    id: number;
    type: string;
    startDate: string;
    endDate: string;
    days: number;
    status: string;
    created: string;
};

type TeamRow = {
    id: number;
    member: string;
    department: string;
    absenceType: string;
    period: string;
    status: string;
};

export default function VacationCalendar() {
    const { t } = useTranslation();

    const [activeTab, setActiveTab] = useState("calendar");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDay, setSelectedDay] = useState(15);
    const [requestType, setRequestType] = useState("vacation");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [reason, setReason] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);

    const requestTypeOptions: SelectOption<string>[] = [
        { value: "vacation", label: t("form.vacationOption") },
        { value: "permission", label: t("form.permissionOption") },
    ];

    const requestRows: RequestRow[] = [
        {
            id: 1,
            type: t("requests.vacation"),
            startDate: "15/07/2026",
            endDate: "24/07/2026",
            days: 8,
            status: t("requests.pending"),
            created: "02/06/2026",
        },
        {
            id: 2,
            type: t("requests.permission"),
            startDate: "04/05/2026",
            endDate: "04/05/2026",
            days: 1,
            status: t("requests.approved"),
            created: "20/04/2026",
        },
        {
            id: 3,
            type: t("requests.vacation"),
            startDate: "13/04/2026",
            endDate: "17/04/2026",
            days: 5,
            status: t("requests.rejected"),
            created: "15/03/2026",
        },
    ];

    const teamRows: TeamRow[] = [
        {
            id: 1,
            member: "María López",
            department: "Marketing",
            absenceType: t("requests.vacation"),
            period: "08/06/2026 - 12/06/2026",
            status: t("requests.approved"),
        },
        {
            id: 2,
            member: "Carlos Martín",
            department: "Producto",
            absenceType: t("requests.permission"),
            period: "18/06/2026",
            status: t("requests.pending"),
        },
        {
            id: 3,
            member: "Ana García",
            department: "Marketing",
            absenceType: t("requests.vacation"),
            period: "22/06/2026 - 26/06/2026",
            status: t("requests.approved"),
        },
    ];

    const requestColumns: TableColumn<RequestRow>[] = [
        {
            key: "type",
            label: t("requests.type"),
            sortable: true,
            render: (row) => (
                <div className="flex items-center gap-2">
                    <Icon
                        value={
                            row.type === t("requests.vacation")
                                ? "calendar"
                                : "fileText"
                        }
                        size={16}
                        className="text-[#7c4545]"
                    />
                    <span>{row.type}</span>
                </div>
            ),
        },
        { key: "startDate", label: t("requests.startDate"), sortable: true },
        { key: "endDate", label: t("requests.endDate"), sortable: true },
        { key: "days", label: t("requests.days"), align: "center" },
        {
            key: "status",
            label: t("requests.status"),
            render: (row) => (
                <Badge
                    pill
                    dot
                    size="sm"
                    variant={
                        row.status === t("requests.approved")
                            ? "success"
                            : row.status === t("requests.pending")
                              ? "warning"
                              : "danger"
                    }
                >
                    {row.status}
                </Badge>
            ),
        },
        { key: "created", label: t("requests.created"), sortable: true },
    ];

    const teamColumns: TableColumn<TeamRow>[] = [
        {
            key: "member",
            label: t("team.member"),
            sortable: true,
            render: (row) => (
                <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#7c4545]/10 text-sm font-semibold text-[#7c4545]">
                        {row.member
                            .split(" ")
                            .map((part) => part[0])
                            .join("")
                            .slice(0, 2)}
                    </div>
                    <span className="font-medium">{row.member}</span>
                </div>
            ),
        },
        { key: "department", label: t("team.department") },
        { key: "absenceType", label: t("team.absenceType") },
        { key: "period", label: t("team.period") },
        {
            key: "status",
            label: t("team.teamStatus"),
            render: (row) => (
                <Badge
                    pill
                    dot
                    size="sm"
                    variant={
                        row.status === t("requests.approved")
                            ? "success"
                            : "warning"
                    }
                >
                    {row.status}
                </Badge>
            ),
        },
    ];

    const requestActions: TableAction<RequestRow>[] = [
        {
            label: t("requests.actions.view"),
            icon: <Icon value="fileText" size={14} />,
            onClick: () => undefined,
            variant: "default",
        },
        {
            label: t("requests.actions.cancel"),
            icon: <Icon value="x" size={14} />,
            onClick: () => undefined,
            variant: "delete",
        },
    ];

    const dayNumbers = Array.from({ length: 30 }, (_, index) => index + 1);

    const handleSubmit = () => {
        setIsModalOpen(false);
        setShowSuccess(true);
        setStartDate("");
        setEndDate("");
        setReason("");
    };

    return (
        <div className="min-h-full bg-slate-50 p-4 dark:bg-[#121212] md:p-6">
            <div className="mx-auto max-w-7xl">
                <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <Title
                        title={t("page.title")}
                        subtitle={t("page.subtitle")}
                        badge={t("page.badge")}
                    />

                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Button
                            variant="outline"
                            text={t("page.todayButton")}
                            startIcon={<Icon value="calendar" size={16} />}
                            onClick={() => setSelectedDay(15)}
                        />
                        <Button
                            variant="primary"
                            text={t("page.requestButton")}
                            startIcon={<Icon value="plus" size={16} />}
                            onClick={() => setIsModalOpen(true)}
                        />
                    </div>
                </div>

                {showSuccess && (
                    <div className="mb-6">
                        <Alert variant="success" value={t("form.submitted")} />
                    </div>
                )}

                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard
                        title={t("stats.available")}
                        value="15"
                        icon="calendar"
                        colorClass="text-[#7c4545]"
                    />
                    <StatCard
                        title={t("stats.used")}
                        value="8"
                        icon="check"
                        colorClass="text-emerald-600"
                    />
                    <StatCard
                        title={t("stats.pending")}
                        value="1"
                        icon="clock"
                        colorClass="text-amber-600"
                    />
                    <StatCard
                        title={t("stats.teamAbsences")}
                        value="3"
                        icon="users"
                        colorClass="text-blue-600"
                    />
                </div>

                <div className="mb-6">
                    <Tabs
                        tabs={[
                            {
                                id: "calendar",
                                label: t("tabs.calendar"),
                                icon: <Icon value="calendar" />,
                            },
                            {
                                id: "requests",
                                label: t("tabs.requests"),
                                icon: <Icon value="fileText" />,
                                badge: "3",
                            },
                            {
                                id: "team",
                                label: t("tabs.team"),
                                icon: <Icon value="users" />,
                                badge: "3",
                            },
                        ]}
                        activeTab={activeTab}
                        onChange={setActiveTab}
                        variant="underline"
                    />
                </div>

                {activeTab === "calendar" && (
                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
                        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#1D1F20] md:p-6">
                            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                                        {t("calendar.title")}
                                    </h2>
                                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                        {t("calendar.subtitle")}
                                    </p>
                                </div>

                                <div className="flex items-center justify-between gap-2">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        tooltip={t("page.previousMonth")}
                                        startIcon={
                                            <Icon
                                                value="chevronLeft"
                                                size={18}
                                            />
                                        }
                                        onClick={() => undefined}
                                    />
                                    <span className="min-w-32 text-center text-sm font-semibold text-slate-900 dark:text-white">
                                        {t("page.currentMonth")}
                                    </span>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        tooltip={t("page.nextMonth")}
                                        startIcon={
                                            <Icon
                                                value="chevronRight"
                                                size={18}
                                            />
                                        }
                                        onClick={() => undefined}
                                    />
                                </div>
                            </div>

                            <div className="mb-2 grid grid-cols-7 gap-1 text-center text-xs font-semibold text-slate-400">
                                <span>{t("calendar.weekdays.monday")}</span>
                                <span>{t("calendar.weekdays.tuesday")}</span>
                                <span>{t("calendar.weekdays.wednesday")}</span>
                                <span>{t("calendar.weekdays.thursday")}</span>
                                <span>{t("calendar.weekdays.friday")}</span>
                                <span>{t("calendar.weekdays.saturday")}</span>
                                <span>{t("calendar.weekdays.sunday")}</span>
                            </div>

                            <div className="grid grid-cols-7 gap-1">
                                <div />
                                <div />
                                {dayNumbers.map((day) => {
                                    const isSelected = selectedDay === day;
                                    const hasVacation =
                                        day === 8 ||
                                        day === 9 ||
                                        day === 10 ||
                                        day === 11 ||
                                        day === 12;
                                    const hasPermission = day === 18;
                                    const isHoliday = day === 29;

                                    return (
                                        <div
                                            key={day}
                                            className={`min-h-16 rounded-xl border p-1 ${
                                                isSelected
                                                    ? "border-[#7c4545] bg-[#7c4545]/5"
                                                    : "border-transparent hover:border-slate-200 dark:hover:border-white/10"
                                            }`}
                                        >
                                            <Button
                                                variant={
                                                    isSelected
                                                        ? "primary"
                                                        : "ghost"
                                                }
                                                size="xs"
                                                text={String(day)}
                                                fullWidth
                                                onClick={() =>
                                                    setSelectedDay(day)
                                                }
                                            />
                                            <div className="mt-1 flex justify-center gap-1">
                                                {hasVacation && (
                                                    <span className="h-1.5 w-1.5 rounded-full bg-[#7c4545]" />
                                                )}
                                                {hasPermission && (
                                                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                                                )}
                                                {isHoliday && (
                                                    <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="mt-6 flex flex-wrap gap-4 border-t border-slate-200 pt-4 dark:border-white/10">
                                <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                                    <span className="h-2.5 w-2.5 rounded-full bg-[#7c4545]" />
                                    {t("calendar.legend.vacation")}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                                    <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                                    {t("calendar.legend.permission")}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                                    <span className="h-2.5 w-2.5 rounded-full bg-slate-400" />
                                    {t("calendar.legend.holiday")}
                                </div>
                            </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-[#1D1F20]">
                            <div className="mb-5 flex items-center gap-2">
                                <Icon
                                    value="calendar"
                                    size={18}
                                    className="text-[#7c4545]"
                                />
                                <h2 className="font-semibold text-slate-900 dark:text-white">
                                    {t("calendar.monthSummary")}
                                </h2>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between border-b border-slate-100 pb-3 dark:border-white/10">
                                    <span className="text-sm text-slate-500">
                                        {t("calendar.workingDays")}
                                    </span>
                                    <span className="font-semibold text-slate-900 dark:text-white">
                                        22
                                    </span>
                                </div>
                                <div className="flex items-center justify-between border-b border-slate-100 pb-3 dark:border-white/10">
                                    <span className="text-sm text-slate-500">
                                        {t("calendar.plannedAbsences")}
                                    </span>
                                    <span className="font-semibold text-slate-900 dark:text-white">
                                        3
                                    </span>
                                </div>
                                <div className="flex items-center justify-between border-b border-slate-100 pb-3 dark:border-white/10">
                                    <span className="text-sm text-slate-500">
                                        {t("calendar.holidays")}
                                    </span>
                                    <span className="font-semibold text-slate-900 dark:text-white">
                                        1
                                    </span>
                                </div>
                            </div>

                            <div className="mt-6 space-y-3">
                                <Badge variant="success" pill dot>
                                    {t("calendar.events.maria")}
                                </Badge>
                                <Badge variant="warning" pill dot>
                                    {t("calendar.events.carlos")}
                                </Badge>
                                <Badge variant="neutral" pill dot>
                                    {t("calendar.events.holiday")}
                                </Badge>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === "requests" && (
                    <Table
                        title={t("requests.title")}
                        data={requestRows}
                        columns={requestColumns}
                        actions={requestActions}
                        searchable
                        searchPlaceholder={t("requests.search")}
                        paginated
                        emptyText={t("empty")}
                        filters={
                            <div className="flex flex-col gap-2 sm:flex-row">
                                <Select
                                    value="all"
                                    onChange={() => undefined}
                                    options={[
                                        {
                                            value: "all",
                                            label: t("requests.allTypes"),
                                        },
                                        {
                                            value: "vacation",
                                            label: t("requests.vacation"),
                                        },
                                        {
                                            value: "permission",
                                            label: t("requests.permission"),
                                        },
                                    ]}
                                    placeholder={t("filters.type")}
                                    size="sm"
                                />
                                <Select
                                    value="all"
                                    onChange={() => undefined}
                                    options={[
                                        {
                                            value: "all",
                                            label: t("requests.allStatuses"),
                                        },
                                        {
                                            value: "approved",
                                            label: t("requests.approved"),
                                        },
                                        {
                                            value: "pending",
                                            label: t("requests.pending"),
                                        },
                                        {
                                            value: "rejected",
                                            label: t("requests.rejected"),
                                        },
                                    ]}
                                    placeholder={t("filters.status")}
                                    size="sm"
                                />
                            </div>
                        }
                        headerRight={
                            <Button
                                variant="primary"
                                size="sm"
                                text={t("page.requestButton")}
                                startIcon={<Icon value="plus" size={15} />}
                                onClick={() => setIsModalOpen(true)}
                            />
                        }
                    />
                )}

                {activeTab === "team" && (
                    <Table
                        title={t("team.title")}
                        data={teamRows}
                        columns={teamColumns}
                        searchable
                        searchPlaceholder={t("requests.search")}
                        paginated
                        emptyText={t("empty")}
                    />
                )}

                <Modal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={t("form.title")}
                    maxWidth="2xl"
                    footer={
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button
                                variant="secondary"
                                text={t("form.cancel")}
                                onClick={() => setIsModalOpen(false)}
                            />
                            <Button
                                variant="primary"
                                text={t("form.submit")}
                                startIcon={
                                    <Icon value="sendHorizontal" size={16} />
                                }
                                onClick={handleSubmit}
                            />
                        </div>
                    }
                >
                    <div className="flex flex-col gap-4">
                        <Select
                            value={requestType}
                            onChange={setRequestType}
                            options={requestTypeOptions}
                            placeholder={t("form.typePlaceholder")}
                        />

                        <Input
                            label={t("form.startDate")}
                            placeholder={t("form.startDatePlaceholder")}
                            value={startDate}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                setStartDate(event.target.value)
                            }
                            required
                        />

                        <Input
                            label={t("form.endDate")}
                            placeholder={t("form.endDatePlaceholder")}
                            value={endDate}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                setEndDate(event.target.value)
                            }
                            required
                        />

                        <Textarea
                            value={reason}
                            onChange={(
                                event: ChangeEvent<HTMLTextAreaElement>
                            ) => setReason(event.target.value)}
                            placeholder={t("form.reasonPlaceholder")}
                            rows={4}
                        />
                    </div>
                </Modal>
            </div>
        </div>
    );
}
